"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ComponentEditSession = void 0;
var tslib_1 = require("tslib");
var core_1 = require("@stylable/core");
var stylable_panel_common_1 = require("@wix/stylable-panel-common");
var selector_configuration_driver_1 = require("./selector-configuration-driver");
var stylable_panel_translation_keys_1 = require("./stylable-panel-translation-keys");
var controller_part_utils_1 = require("./utils/controller-part-utils");
var START_PAGE = stylable_panel_common_1.StylePanelPage.CustomizationPanel;
var ComponentEditSession = /** @class */ (function () {
    function ComponentEditSession(siteStylesheetPath, stylesheetPath, variant, componentConfig, stylableDriver, changeDriver, presetStylesheetPaths, aggregationPaths) {
        var _this = this;
        this.siteStylesheetPath = siteStylesheetPath;
        this.stylesheetPath = stylesheetPath;
        this.variant = variant;
        this.componentConfig = componentConfig;
        this.stylableDriver = stylableDriver;
        this.changeDriver = changeDriver;
        this.presetStylesheetPaths = presetStylesheetPaths;
        this.aggregationPaths = aggregationPaths;
        this.getStylableImport = function (metaImports) {
            return metaImports.find(function (_a) {
                var defaultExport = _a.defaultExport, named = _a.named;
                return defaultExport === _this.componentName || Object.values(named).includes(_this.componentName);
            });
        };
        this.getNamedImports = function (varNames, format) {
            return varNames
                .map(function (varName) { return format(varName, _this.componentName); })
                .reduce(function (acc, name) {
                acc[name] = name;
                return acc;
            }, {});
        };
        this.selector = ".".concat(variant);
        this.selectedElement = ".".concat(variant);
        this.overrideStateSelector = ".".concat(variant);
        this.defaultPreviewProps = componentConfig.previewProps || {};
        this.componentName = this.componentConfig.id;
        this.elements = stylableDriver.getPseudoElementsDeep(this.stylesheetPath, this.selector);
        this.controllerPartTypes = (0, controller_part_utils_1.elementTreeSelectors)(this.elements).reduce(function (controllerPartTypes, selector) {
            var controllerPartType = stylableDriver.getControllerPartType(_this.stylesheetPath, selector);
            if (controllerPartType) {
                controllerPartTypes[selector] = controllerPartType
                    .replace(/^"(.+(?="$))"$/, '$1') // remove surrounding quotes
                    .split(',')
                    .map(function (partType) { return partType.trim(); });
            }
            return controllerPartTypes;
        }, {});
        this.selectorConfiguration = new selector_configuration_driver_1.SelectorConfigurationDriver(componentConfig.selectorConfiguration || {}, this.selector, {
            hover: { nameKey: stylable_panel_translation_keys_1.StylablePanelTranslationKeys.states.native.hoverLabel },
            focus: { nameKey: stylable_panel_translation_keys_1.StylablePanelTranslationKeys.states.native.focusLabel },
            disabled: { nameKey: stylable_panel_translation_keys_1.StylablePanelTranslationKeys.states.native.disabledLabel },
            active: { nameKey: stylable_panel_translation_keys_1.StylablePanelTranslationKeys.states.native.activeLabel },
        });
        if (this.componentConfig.initialElement) {
            this.selector = this.selectorConfiguration.selectorToVariant(this.componentConfig.initialElement);
            this.selectedElement = this.getSelectorWithoutStates();
        }
        var stylesheet = this.getStylesheet();
        stylesheet && stylableDriver.stylable.transform(stylesheet.getMeta());
        this.updateComputed();
        this.presetSectionLayouts = componentConfig.presetSectionLayouts || {
            static: {},
            theme: {},
            user: {},
        };
        this.changeDriver.openSession(this);
        this.page = START_PAGE;
    }
    ComponentEditSession.prototype.getSourceCSS = function () {
        var sheetDriver = this.getStylesheet();
        return (sheetDriver === null || sheetDriver === void 0 ? void 0 : sheetDriver.source) || '';
    };
    /**
     *
     * @param selector to change to
     * @return true if state was updated
     */
    ComponentEditSession.prototype.setSelector = function (selector) {
        var _a;
        var oldSelector = this.selector;
        var oldOverrideStateSelector = this.overrideStateSelector;
        this.selector = selector;
        this.selectedElement = this.getSelectorWithoutStates();
        this.updateComputed();
        if (oldOverrideStateSelector === this.overrideStateSelector) {
            var stateMatch = (_a = oldSelector.match(/[^:]:([^:]+)/)) === null || _a === void 0 ? void 0 : _a[1];
            if (stateMatch) {
                this.selector = this.selectedElement.replace(this.overrideStateSelector, "".concat(this.overrideStateSelector, ":").concat(stateMatch));
            }
        }
        return oldOverrideStateSelector !== this.overrideStateSelector;
    };
    ComponentEditSession.prototype.updateComputed = function () {
        this.updateOverrideStateSelector();
        var sheetDriver = this.getStylesheet();
        if (!sheetDriver) {
            this.computed = { states: [], usedStates: [] };
            return;
        }
        this.computed = {
            states: sheetDriver.getVariantStates(this.overrideStateSelector),
            usedStates: sheetDriver.getVariantUsedStates(this.overrideStateSelector),
        };
    };
    ComponentEditSession.prototype.getSelectorWithoutStates = function () {
        return (0, selector_configuration_driver_1.stripStates)(this.selector);
    };
    ComponentEditSession.prototype.getStylesheet = function () {
        return this.stylableDriver.getStylesheet(this.stylesheetPath);
    };
    ComponentEditSession.prototype.getSelectorDeclarations = function (selector) {
        return this.stylableDriver.aggregateSelectorDeclarations(tslib_1.__spreadArray([this.stylesheetPath], tslib_1.__read((this.aggregationPaths || [])), false), this.selectorConfiguration.selectorToVariant(selector));
    };
    ComponentEditSession.prototype.setSelectorDeclarations = function (selectorOrActions, declarations) {
        var _this = this;
        var simpleChangeActions = typeof selectorOrActions === 'string' ? [{ selector: selectorOrActions, declarations: declarations }] : selectorOrActions;
        this.changeDriver.changeSelectors(simpleChangeActions.map(function (simpleChangeAction) { return ({
            values: simpleChangeAction.declarations,
            selector: _this.selectorConfiguration.selectorToVariant(simpleChangeAction.selector),
        }); }));
    };
    ComponentEditSession.prototype.ensureImportedCssVars = function (varNames, format) {
        if (format === void 0) { format = function (varName) { return "--".concat(varName); }; }
        var stylesheet = this.getStylesheet();
        if (!stylesheet) {
            throw new Error("missing stylesheet: ".concat(this.stylesheetPath));
        }
        var ast = stylesheet.AST;
        var meta = stylesheet.getMeta();
        var stylableImport = this.getStylableImport(meta.imports);
        if (!stylableImport) {
            throw new Error("could not find component import: ".concat(this.componentName));
        }
        var named = this.getNamedImports(varNames, format);
        var diagnostics = (0, core_1.ensureStylableImports)(ast, [{ request: stylableImport.request, named: named }], {
            newImport: 'none',
        }).diagnostics;
        if (diagnostics.reports.length) {
            var reports = diagnostics.reports.map(function (report) { return report.message; }).join('\n');
            throw new Error("could not ensure imports:\n".concat(reports));
        }
    };
    ComponentEditSession.prototype.applyChangesToStylesheets = function (requestedChange) {
        this.changeDriver.applyStylesheetChange(requestedChange);
    };
    ComponentEditSession.prototype.updateOverrideStateSelector = function () {
        this.overrideStateSelector = this.selectorConfiguration.getPrimeElement(this.selectedElement);
    };
    return ComponentEditSession;
}());
exports.ComponentEditSession = ComponentEditSession;
//# sourceMappingURL=editor-edit-session.js.map